// React Compiler opt-out: this file still reads mutable external state during render
// (legacy store object getters and/or current-time reads) that the useFluxStore bridge
// doesn't cover. Remove after migrating those reads (see flux-bridge-conversion skill).
'use no memo';

import {Icon, Text, WithTooltip} from '@albert-io/atomic';
import {useDroppable} from '@dnd-kit/core';
import {useShallow} from 'zustand/react/shallow';
import type {QuestionModelV3} from '@albert-io/models';
import React, {useEffect} from 'react';
import useQuestionStore from 'client/components/QuestionTypes/V2/useQuestionStores';
import {WORD_BANK_ID} from '../store';
import {MovableItem} from '../../shared/MovableItem.react';
import {getSmallSupplement, SMALL_SUPPLEMENT_WRAPPER_CLASS} from '../../shared/DraggableChip';

interface Props {
  question: QuestionModelV3;
  handleItemClick: (itemId: string) => void;
  handleBankClick?: () => void;
}

const WordBank = ({question, handleItemClick, handleBankClick}: Props) => {
  const useStoreHook = useQuestionStore<'item-bank'>(question);

  const {setNodeRef, node} = useDroppable({id: WORD_BANK_ID});

  // oxlint-disable-next-line react/react-compiler -- useStoreHook comes from setUpQuestionStore's registry (cached zustand hook keyed by store name); its identity is stable for a mounted component
  const {wordBankItems, activeItemId, isDragging, isSubmitted} = useStoreHook(
    useShallow((state) => ({
      wordBankItems: state.wordBank,
      activeItemId: state.activeItemId,
      isDragging: state.isDragging,
      isSubmitted: state.isSubmitted
    }))
  );

  useEffect(() => {
    const handleClickInside = (e: MouseEvent) => {
      // only handle clicks if an item is active
      if (activeItemId === null) return;
      const target = e.target as Element;
      const isOnItem = target.closest('[data-item-id]') !== null;
      if (!isOnItem && node.current && node.current.contains(target)) {
        handleBankClick?.();
      }
    };
    document.addEventListener('click', handleClickInside);
    return () => {
      document.removeEventListener('click', handleClickInside);
    };
  }, [handleBankClick, node, activeItemId]);

  return (
    <div
      ref={setNodeRef}
      className='gap-x1-half rounded-x1 background-bold! p-x1-half flex flex-col border-0'
    >
      <div className='flex items-center justify-between'>
        <Text size='s'>Item Bank:</Text>
        <WithTooltip content='Click or drag an item to move it to the correct answer'>
          <Icon icon='question-circle' color='tertiary' iconStyle='regular' />
        </WithTooltip>
      </div>
      <div className='gap-x1-half flex flex-wrap'>
        {wordBankItems
          .filter((item) => item.placedInSlot === null)
          .map((item) => {
            const isSupplement = !!getSmallSupplement(item.value);
            return (
              <div key={item.id} className={isSupplement ? SMALL_SUPPLEMENT_WRAPPER_CLASS : ''}>
                <MovableItem
                  item={item}
                  isActive={activeItemId === item.id}
                  isDragging={isDragging}
                  onClick={() => handleItemClick(item.id)}
                  isSubmitted={isSubmitted}
                  activeItemId={activeItemId}
                />
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default WordBank;
//
